/**
 * Hook para cache de participantes de grupos
 *
 * Mantém os participantes em memória durante a sessão para evitar
 * chamadas repetidas à API quando o usuário alterna entre grupos.
 *
 * IMPORTANTE: O cache vive em escopo de MÓDULO (singleton), portanto
 * persiste entre montagens/desmontagens do GroupSidebar enquanto a aba
 * do navegador estiver aberta. Só é zerado em refresh da página ou via
 * clearCache(). Isso garante que, após o usuário clicar em "Atualizar
 * participantes" para um grupo, ao reabrir o mesmo grupo a lista
 * apareça instantaneamente — sem rede, sem RPC e sem toast.
 *
 * Funcionalidades:
 * - Cache singleton em Map<groupId, participantes>
 * - TTL (Time To Live) de 5 minutos por padrão
 * - Forçar refresh ignorando cache
 * - Limpar cache de grupo específico ou todo cache
 */

import { useCallback } from 'react';
import { useGroupUtils } from './useGroupUtils';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

// Logger com namespace específico para o cache de participantes
const log = createLogger('ParticipantsCache');

// Interface para participante de grupo
interface GroupParticipant {
  id: string;
  participant_phone: string;
  display_name: string | null;
  is_admin: boolean;
  is_super_admin: boolean;
  joined_at: string;
}

// Estrutura de entrada no cache
interface CacheEntry {
  participants: GroupParticipant[];
  timestamp: number;
}

// TTL padrão: 5 minutos (300000ms)
const DEFAULT_CACHE_TTL = 5 * 60 * 1000;

/**
 * Cache singleton em escopo de módulo.
 *
 * Compartilhado por toda a aplicação durante a sessão do navegador,
 * sobrevivendo a montagens/desmontagens do componente que usa o hook.
 */
const participantsCache = new Map<string, CacheEntry>();

/**
 * Hook para gerenciar cache de participantes de grupos WhatsApp
 *
 * @param cacheTTL - Tempo de vida do cache em milissegundos (padrão: 5 minutos)
 * @returns Funções para buscar, invalidar e limpar cache
 */
export function useGroupParticipantsCache(cacheTTL: number = DEFAULT_CACHE_TTL) {
  const { getGroupParticipants } = useGroupUtils();

  /**
   * Verifica se entrada do cache ainda é válida (não expirou)
   */
  const isCacheValid = useCallback((groupId: string): boolean => {
    const entry = participantsCache.get(groupId);
    if (!entry) return false;

    const now = Date.now();
    const isValid = (now - entry.timestamp) < cacheTTL;

    if (!isValid) {
      // Remove entrada expirada
      participantsCache.delete(groupId);
      log.debug(`Cache expirado para grupo ${groupId.slice(0, 8)}`);
    }

    return isValid;
  }, [cacheTTL]);

  /**
   * Obtém participantes do cache se válido
   */
  const getFromCache = useCallback((groupId: string): GroupParticipant[] | null => {
    if (isCacheValid(groupId)) {
      return participantsCache.get(groupId)?.participants || null;
    }
    return null;
  }, [isCacheValid]);

  /**
   * Salva participantes no cache
   */
  const saveToCache = useCallback((groupId: string, participants: GroupParticipant[]) => {
    participantsCache.set(groupId, {
      participants,
      timestamp: Date.now(),
    });
    log.debug(`Participantes salvos no cache - grupo ${groupId.slice(0, 8)}, count: ${participants.length}`);
  }, []);

  /**
   * Busca participantes - usa cache se disponível, senão busca da API
   *
   * @param inboxId - ID da inbox
   * @param groupId - UUID do grupo no banco
   * @param externalId - ID externo do grupo (para API)
   * @param forceRefresh - Se true, ignora cache e busca da API
   */
  const fetchParticipants = useCallback(async (
    inboxId: string,
    groupId: string,
    externalId: string,
    forceRefresh: boolean = false
  ): Promise<{ participants: GroupParticipant[]; fromCache: boolean }> => {

    // Verifica cache primeiro (se não for forceRefresh)
    if (!forceRefresh) {
      const cached = getFromCache(groupId);
      if (cached) {
        log.debug(`Cache hit para grupo ${groupId.slice(0, 8)}`);
        return { participants: cached, fromCache: true };
      }
    }

    log.debug(`Cache miss - buscando da API para ${groupId.slice(0, 8)}`);

    // Busca da API
    const data = await getGroupParticipants(inboxId, externalId);
    const participantsList = Array.isArray(data) ? data : (data?.participants || []);

    // Atualizar participant_count no banco via RPC server-side (sem UPDATE direto)
    if (participantsList.length > 0) {
      await supabase.rpc('update_chat_group_participant_count_safe', {
        p_group_id: groupId,
        p_count: participantsList.length,
      });
    }

    // Salva no cache
    saveToCache(groupId, participantsList);

    return { participants: participantsList, fromCache: false };
  }, [getGroupParticipants, getFromCache, saveToCache]);

  /**
   * Limpa cache de um grupo específico
   */
  const invalidateGroup = useCallback((groupId: string) => {
    participantsCache.delete(groupId);
    log.debug(`Cache invalidado para grupo ${groupId.slice(0, 8)}`);
  }, []);

  /**
   * Limpa todo o cache
   */
  const clearCache = useCallback(() => {
    participantsCache.clear();
    log.debug('Cache limpo completamente');
  }, []);

  /**
   * Retorna estatísticas do cache (para debug)
   */
  const getCacheStats = useCallback(() => {
    return {
      size: participantsCache.size,
      groups: Array.from(participantsCache.keys()).map(id => id.slice(0, 8)),
    };
  }, []);

  return {
    fetchParticipants,
    invalidateGroup,
    clearCache,
    getCacheStats,
    getFromCache,
  };
}
